import React, { useState, useEffect, useRef } from 'react';
import { Alert, Form } from 'react-bootstrap';
import classes from './OnboardingComponents.module.scss'

const Portfolio = (props) => {

    const [profile, setProfile] = useState({});

    const [portfolios, setPortfolios] = useState(props.portfolios || []);
    const [portfolioEditVisible, setPortfolioEditVisible] = useState(false);
    const [portfolioSelection, setPortfolioSelection] = useState([{ id: '1', icon: 'linkedin' }, { id: '2', icon: 'github' }, { id: '3', icon: 'ryla' }, { id: '4', icon: 'instagram' }, { id: '5', icon: 'fiverr' }, { id: '6', icon: 'medium' }, { id: '7', icon: 'upwork' }, { id: '8', icon: 'generic' }]);
    const [selectedPortfolio, setSelectedPortfolio] = useState(null);
    const isSelectedPortfolioInPortfolios = selectedPortfolio && portfolios?.some(portfolio => portfolio.icon === selectedPortfolio.icon);
    const portfolioEditorRef = useRef(null);
    const PREFIX = "https://";
    const initialUrl = selectedPortfolio ? selectedPortfolio.url || PREFIX : PREFIX;
    const urlPattern = new RegExp(
        /^(https:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}([/?].*)?$/
    );
    const [isValidUrl, setIsValidUrl] = useState(true);
    const [url, setUrl] = useState(initialUrl);

    const [experience, setExperience] = useState(profile.experience || '');

    const [errors, setErrors] = useState({});

    useEffect(() => {
        const holder = JSON.parse(sessionStorage.getItem('profile'));
        const updatedProfile = {
            ...holder,
            experience: holder && holder.experience ? holder.experience : props.experience,
            portfolios: holder && holder.portfolios ? holder.portfolios : props.portfolios
        }
        setProfile(updatedProfile);
        setExperience(updatedProfile.experience || props.experience);
        setPortfolios(updatedProfile.portfolios || props.portfolios);
        sessionStorage.setItem('profile', JSON.stringify(updatedProfile));
    }, [props.experience, props.portfolios])


    // LinkedIn is always the first portfolio item
    const sortPortfolios = (portfolios) => {
        return portfolios?.sort((a, b) => {
            if (a.icon === 'linkedin') return -1;
            if (b.icon === 'linkedin') return 1;
            return 0;
        });
    };


    const handleExperienceChange = (event) => {
        const updatedProfile = { ...profile, experience: event.target.value };
        setExperience(event.target.value);

        sessionStorage.setItem('profile', JSON.stringify(updatedProfile));
        setErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            delete newErrors.experience;
            return newErrors;
        });
    };


    /**
       * The `handlePortfolioClick` function toggles the visibility of the portfolio editor and updates
       * the selected portfolio with full details if a portfolio is provided.
       * @param portfolio - The `portfolio` parameter in the `handlePortfolioClick` function likely
       * represents a specific portfolio item that the user has clicked on or interacted with. This could
       * be an object containing details about the portfolio, such as its icon, name, description, or any
       * other relevant information.
       * @param edit - The `edit` parameter in the `handlePortfolioClick` function is a boolean value
       * that indicates whether the user is currently in edit mode for the portfolio.
       */
    const handlePortfolioClick = (portfolio, edit) => {
        let fullPortfolio = null;
        // Toggle the visibility of the portfolio editor
        if (!edit)
            setPortfolioEditVisible(prevState => !prevState);
        // If a portfolio is provided, update the selectedPortfolio with the full details from the main list
        if (portfolio) {
            fullPortfolio = portfolios?.find(p => p.icon === portfolio.icon) || portfolio;
            setSelectedPortfolio(fullPortfolio);
        } else {
            // Optionally handle the case where no portfolio is provided
            setSelectedPortfolio(fullPortfolio);
        }
    };

    /**
       * The function `handlePortfoliosUpdate` updates a list of portfolios with a selected portfolio,
       * sorting them and toggling the visibility of the portfolio editor if a valid URL is provided.
       */
    const handlePortfoliosUpdate = () => {
        // Validate the URL
        setIsValidUrl(urlPattern.test(url));
        // Update the selected portfolio with the URL and add it to the list of portfolios
        if ("icon" in selectedPortfolio && urlPattern.test(url)) {
            setPortfolios(prevPortfolios => {
                const updatedList = [...(prevPortfolios || [])];
                const index = updatedList.findIndex(p => p.icon === selectedPortfolio.icon);

                if (index > -1) {
                    updatedList[index] = selectedPortfolio;
                } else {
                    updatedList.push(selectedPortfolio);
                }

                const sorted = sortPortfolios(updatedList);
                const updatedProfile = { ...profile, portfolios: sorted };
                setProfile(updatedProfile);
                sessionStorage.setItem('profile', JSON.stringify(updatedProfile));
                return sorted;
            });
            // Toggle the visibility of the portfolio editor
            setPortfolioEditVisible(prevVisible => !prevVisible);
        }

    };

    // Used by the portfolio editor to update the URL of the selected portfolio
    const handleUrlChange = (event) => {
        let newUrl = event.target.value.trim();

        // Remove "http://" and "https://" (especilaly when pasting a URL with "https://")
        newUrl = newUrl.replace(/^https?:\/\//, '');

        if (newUrl === "https:/") return;
        // Ensure the URL starts with "https://"
        if (!newUrl.startsWith(PREFIX)) {
            newUrl = PREFIX + newUrl.replace(/^https?:\/\//, '');
        }

        // Prevent deletion beyond the prefix
        if (newUrl.length < PREFIX.length) {
            newUrl = PREFIX;
        }

        setSelectedPortfolio(currentPortfolio => ({
            ...currentPortfolio,
            url: newUrl
        }));
        setUrl(newUrl);
    };

    const handleUrlSelection = (e) => {
        // Prevent selecting the prefix
        const input = e.target;
        if (input.selectionStart < PREFIX.length) {
            input.setSelectionRange(PREFIX.length, PREFIX.length);
        }
    };

    const handleMouseUp = (e) => handleUrlSelection(e);

    // Used by the portfolio editor to delete a portfolio
    const handleDeletePortfolioClick = (event, selectedPortfolio) => {
        event.stopPropagation(); // Stop event from propagating to parent
        // Delete portfolio
        const updatedPortfolios = portfolios.filter((portfolio) => portfolio.id !== selectedPortfolio.id);
        setPortfolios(updatedPortfolios);
        sessionStorage.setItem('profile', JSON.stringify({ ...profile, portfolios: updatedPortfolios }));
    };



    return (
        <div className={classes.portfolioContainer}>
            <h1 className={classes.title}>Show us that you exist</h1>
            <div className={classes.subtitle}>Add links to prove your existence, and a summary of your experience helps too.</div>
            <div className={classes.introText}>External Links</div>
            <div className={classes.portfolioSection}>
                <div className={classes.portfolioVertical}>
                    <div className={classes.portfolioLinksContainer}>
                        <div className={classes.portfolioLinksInnerContainer}>
                            {portfolios?.length > 0 ? (
                                portfolios.map((portfolio, index) => (
                                    <div >
                                        <div className={classes.portfolioLink} key={index} onClick={() => handlePortfolioClick(portfolio)} style={{ backgroundImage: `url(/portfolio-icons/${portfolio?.icon}.png)` }}>
                                            <div className={classes.deleteButton} onClick={(e) => handleDeletePortfolioClick(e, portfolio)} />
                                        </div>
                                        <div className={classes.portfolioLinkText}>
                                            {portfolio.icon.charAt(0).toUpperCase() + portfolio.icon.slice(1)}
                                        </div>
                                    </div>

                                ))
                            ) : (
                                <div>No proof of outside existence.</div>
                            )}
                            <div className={`${classes.plusIcon} ${classes.portfolioLink}`} onClick={() => handlePortfolioClick(false, false)} />
                        </div>
                    </div>
                    <div className={`${classes.portfolioEditor} ${portfolioEditVisible ? `${classes.visible} ${classes.fadeInDownMenu} ${classes.animatedFast}` : ''}`} ref={portfolioEditorRef}>
                        <div className={classes.portfolioEditorInner}>
                            Choose a platform and enter a link.
                            <div className={classes.portfolioLinksContainer}>
                                <div className={classes.portfolioLinksInnerContainer}>
                                    {portfolioSelection.map((portfolio, index) => (
                                        <div className={classes.portfolioLinkContainer}>
                                            <div
                                                className={`${classes.portfolioLink} ${selectedPortfolio === portfolio ? classes.selected : ''}`}
                                                key={index}
                                                onClick={() => handlePortfolioClick({ ...portfolio, url: portfolio.url || "" }, true)}
                                                style={{ backgroundImage: `url(/portfolio-icons/${portfolio.icon}.png)` }}
                                            />
                                            <div className={classes.portfolioLinkText}>
                                                {portfolio.icon.charAt(0).toUpperCase() + portfolio.icon.slice(1)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className={classes.editPortfolio}>
                                <div className={classes.portfolioLink} style={{ backgroundImage: selectedPortfolio ? `url(/portfolio-icons/${selectedPortfolio.icon}.png)` : 'none' }} />
                                <Form.Control
                                    type="text"
                                    value={selectedPortfolio ? selectedPortfolio.url || "" : ""}
                                    onChange={handleUrlChange}
                                    placeholder="https://"
                                    className={classes.potfolioUrlInput}
                                    onMouseUp={handleMouseUp}
                                    onFocus={handleUrlSelection}
                                    disabled={!selectedPortfolio || "icon" in selectedPortfolio === false}
                                />
                                {
                                    isSelectedPortfolioInPortfolios ?
                                        (
                                            <div className={classes.updatePortfolioIcon} onClick={handlePortfoliosUpdate}>
                                                Update
                                            </div>
                                        ) : (
                                            <div className={`${classes.plusIcon} ${classes.portfolioLink}`} onClick={handlePortfoliosUpdate} />
                                        )
                                }
                            </div>
                            {!isValidUrl && (
                                <Alert variant="danger" className={classes.urlErrorMessage}>
                                    Please enter a valid URL.
                                </Alert>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className={classes.introText}>Experience</div>
            <div className={`${classes.profileSkillset} ${classes.profileDescriptionCard}`}>
                <div className={classes.textAreaWrapper}>
                    <textarea
                        name="experience"
                        rows="5"
                        cols="40"
                        maxLength="350"
                        value={experience}
                        placeholder={"- List specific skills that make you well-suited to work with other people (e.g., web development, marketing, project management, etc.).\n" +
                            "- Share relevant professional experiences or projects and/or startups that showcase your skills and capabilities. \n" +
                            "- Mention any self-taught skills or knowledge you've gained."}
                        onChange={handleExperienceChange}
                        style={{ resize: 'none' , fontSize: '16px'}}
                    ></textarea>
                    <span className={classes.largeCharCounter}>{experience.length}/350</span>
                </div>
            </div>
        </div>

    );

};

export default Portfolio;